<template>
  <div class="create-upgrade-plan">
    <!-- ====== Hero ====== -->
    <div class="info-hero">
      <div class="hero-bg">
        <svg viewBox="0 0 800 200" preserveAspectRatio="none" class="hero-svg">
          <circle cx="100" cy="100" r="180" fill="rgba(255,255,255,0.05)" />
          <circle cx="650" cy="40" r="150" fill="rgba(8,145,178,0.22)" />
          <path
            d="M 0 150 Q 200 100 400 130 T 800 110"
            stroke="rgba(255,255,255,0.10)"
            stroke-width="1"
            fill="none"
          />
          <path
            d="M 0 170 Q 220 140 420 160 T 800 140"
            stroke="rgba(255,255,255,0.05)"
            stroke-width="1"
            fill="none"
          />
        </svg>
      </div>

      <button class="back-btn" @click="router.back()">
        <el-icon><ArrowLeft /></el-icon>
        <span>返回</span>
      </button>

      <div class="hero-main">
        <div class="hero-avatar">
          <el-icon><Promotion /></el-icon>
        </div>
        <div class="hero-text">
          <div class="hero-tag">CREATE UPGRADE PLAN · 创建升级计划</div>
          <h1 class="hero-title">{{ $t("r.create_upgrade_plan") }}</h1>
          <div v-if="ship" class="hero-subtitle">目标船舶：{{ ship.name }}</div>
        </div>
      </div>
    </div>

    <!-- ====== Step 1: 船舶信息 ====== -->
    <div v-if="shipId" class="step-card">
      <div class="step-card-header">
        <div class="step-badge done">
          <el-icon v-if="ship"><CircleCheck /></el-icon>
          <span v-else>1</span>
        </div>
        <div class="step-card-title">
          <h2>{{ $t("CUPV.shipInfo") }}</h2>
          <span v-if="ship" class="title-extra"> - {{ ship.name }}</span>
        </div>
        <span class="step-status" :class="{ done: ship }">
          {{ ship ? "已加载" : "加载中" }}
        </span>
      </div>
      <div v-if="ship" class="step-card-body">
        <div class="ship-info-grid">
          <div class="info-card">
            <div class="info-icon">
              <el-icon><Ship /></el-icon>
            </div>
            <div class="info-body">
              <span class="info-label">船舶名称</span>
              <span class="info-value">{{ ship.name }}</span>
            </div>
          </div>
          <div class="info-card">
            <div class="info-icon info-icon-cyan">
              <el-icon><Document /></el-icon>
            </div>
            <div class="info-body">
              <span class="info-label">船舶 ID</span>
              <span class="info-value">{{ ship.id }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ====== Step 2: 文件选择 ====== -->
    <div v-if="ship" class="step-card">
      <div class="step-card-header">
        <div class="step-badge" :class="{ done: fileList.length > 0 }">
          <el-icon v-if="fileList.length > 0"><CircleCheck /></el-icon>
          <span v-else>2</span>
        </div>
        <div class="step-card-title">
          <h2>{{ $t("CUPV.selectFile") }}</h2>
          <span v-if="fileList.length > 0" class="title-extra">
            - {{ fileList.length }}{{ $t("CUPV.fileCount") }}
          </span>
        </div>
        <span class="step-status" :class="{ done: fileList.length > 0 }">
          {{ fileList.length > 0 ? "已选择" : "待选择" }}
        </span>
      </div>
      <div class="step-card-body">
        <el-upload
          v-model:file-list="fileList"
          class="upload-demo"
          drag
          multiple
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :auto-upload="false"
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            {{ $t("CUPV.file.beforeText") }}
            <em>{{ $t("CUPV.file.text") }}</em>
            {{ $t("CUPV.file.afterText") }}
          </div>
          <template #tip>
            <div class="el-upload__tip">
              {{ $t("CUPV.file.sizeTip") }}
            </div>
          </template>
        </el-upload>
      </div>
    </div>

    <!-- ====== Step 3: 升级计划 ====== -->
    <div v-if="fileList.length > 0" ref="step3Card" class="step-card">
      <div class="step-card-header">
        <div
          class="step-badge"
          :class="{ done: upgradeSelection && isValidPlanSet() }"
        >
          <el-icon v-if="upgradeSelection && isValidPlanSet()"
            ><CircleCheck
          /></el-icon>
          <span v-else>3</span>
        </div>
        <div class="step-card-title">
          <h2>{{ $t("CUPV.upgradeInfo") }}</h2>
        </div>
        <span
          class="step-status"
          :class="{ done: upgradeSelection && isValidPlanSet() }"
        >
          {{ upgradeSelection && isValidPlanSet() ? "已配置" : "待配置" }}
        </span>
      </div>
      <div class="step-card-body">
        <el-radio-group v-model="upgradeSelection" class="upgrade-radio-group">
          <el-radio :value="1" border>
            {{ $t("CUPV.endpointUpgrade") }}
          </el-radio>
          <el-radio :value="2" border>
            {{ $t("CUPV.deviceUpgrade") }}
          </el-radio>
        </el-radio-group>

        <!-- 配置缺失项实时提示（替代"无提示点不了"的体验） -->
        <div v-if="planIssues.length" class="plan-issues">
          <el-icon><WarningFilled /></el-icon>
          <div class="plan-issues-body">
            <div class="plan-issues-title">
              升级信息还未填写完整，请补全以下内容：
            </div>
            <ul>
              <li v-for="(issue, i) in planIssues" :key="i">{{ issue }}</li>
            </ul>
          </div>
        </div>

        <div v-if="upgradeSelection == 2" class="svc004-section">
          <div class="plan-table-head">
            <div class="col-title">#</div>
            <div class="col">{{ $t("CUPV.planHeader.devType") }}</div>
            <div class="col">{{ $t("CUPV.planHeader.devName") }}</div>
            <div class="col">{{ $t("CUPV.planHeader.subInfo") }}</div>
            <div class="col">{{ $t("CUPV.planHeader.subLv") }}</div>
            <div class="col col-help">
              {{ $t("CUPV.planHeader.lv3") }}
              <el-tooltip
                placement="top"
                effect="light"
                :show-after="150"
                popper-class="subid-help-popper"
              >
                <template #content>
                  <div class="tt-title">三级/二级/一级序号填写说明</div>
                  <div class="tt-row">
                    <span class="tt-lv">层级 3</span>
                    三级序号输入多个编号，如 1、5 → 1-*-*、5-*-*
                  </div>
                  <div class="tt-row">
                    <span class="tt-lv">层级 2</span>
                    固定一个三级编号，二级序号输入多个 如 3 + 1、4 →
                    3-1-*、3-4-*
                  </div>
                  <div class="tt-row">
                    <span class="tt-lv">层级 1</span>
                    固定三级、二级编号，一级序号输入多个 如 1-2 + 7、15 →
                    1-2-7、1-2-15
                  </div>
                  <div class="tt-sep"></div>
                  <div class="tt-row">
                    · 编号范围 1~255，多值框输入后回车添加
                  </div>
                  <div class="tt-row">· 支持粘贴编号列表批量添加</div>
                  <div class="tt-row">
                    · 粘贴完整地址时自动按层级与上级编号筛选提取
                  </div>
                </template>
                <el-icon class="col-help-icon"><QuestionFilled /></el-icon>
              </el-tooltip>
            </div>
            <div class="col col-help">
              {{ $t("CUPV.planHeader.lv2") }}
              <el-tooltip
                placement="top"
                effect="light"
                :show-after="150"
                popper-class="subid-help-popper"
              >
                <template #content>
                  <div class="tt-title">二级序号填写说明</div>
                  <div class="tt-row">
                    <span class="tt-lv">层级 2</span>
                    输入多个二级编号（三级序号已固定），如 1、4
                  </div>
                  <div class="tt-row">
                    <span class="tt-lv">层级 1</span>
                    仅固定一个二级编号，一级序号另输入多个
                  </div>
                  <div class="tt-row">
                    <span class="tt-lv">层级 3</span>
                    本列不使用（禁用）
                  </div>
                  <div class="tt-sep"></div>
                  <div class="tt-row">
                    · 编号范围 1~255，多值框输入后回车添加
                  </div>
                  <div class="tt-row">
                    · 支持粘贴编号列表或完整地址（自动筛选提取）
                  </div>
                </template>
                <el-icon class="col-help-icon"><QuestionFilled /></el-icon>
              </el-tooltip>
            </div>
            <div class="col col-help">
              {{ $t("CUPV.planHeader.lv1") }}
              <el-tooltip
                placement="top"
                effect="light"
                :show-after="150"
                popper-class="subid-help-popper"
              >
                <template #content>
                  <div class="tt-title">一级序号填写说明</div>
                  <div class="tt-row">
                    <span class="tt-lv">层级 1</span>
                    输入多个一级编号（三级、二级已固定），如 7、15
                  </div>
                  <div class="tt-row">
                    <span class="tt-lv">层级 2/3</span>
                    本列不使用（禁用）
                  </div>
                  <div class="tt-sep"></div>
                  <div class="tt-row">· 编号范围 1~255，输入后回车添加</div>
                  <div class="tt-row">
                    · 支持粘贴编号列表或完整地址（自动筛选提取）
                  </div>
                </template>
                <el-icon class="col-help-icon"><QuestionFilled /></el-icon>
              </el-tooltip>
            </div>
            <div class="col">{{ $t("CUPV.planHeader.fileId") }}</div>
            <div class="col col-action">{{ $t("CUPV.planHeader.delete") }}</div>
          </div>
          <div
            v-for="(plan, index) in svc004PlanList"
            :key="index"
            class="plan-row"
          >
            <div class="col-title">
              <span class="row-index">{{ index + 1 }}</span>
            </div>
            <div class="col">
              <el-select
                v-model="plan.devType"
                style="width: 100%"
                @change="onDevTypeChange(plan)"
              >
                <el-option
                  v-for="dt in deviceTypeList"
                  :key="dt"
                  :label="dt"
                  :value="dt"
                />
              </el-select>
            </div>
            <div class="col">
              <el-select
                v-model="plan.devName"
                style="width: 100%"
                :disabled="!plan.devType"
              >
                <el-option
                  v-for="device in getDeviceNamesByType(plan.devType)"
                  :key="device.name"
                  :label="translateName(device.name)"
                  :value="device.name"
                />
              </el-select>
            </div>
            <div class="col">
              <el-select
                v-if="plan.devType === 'BMS'"
                v-model="plan.subInfo"
                style="width: 100%"
                :disabled="!plan.devType"
                @change="onSubInfoChange(plan)"
              >
                <el-option
                  v-for="(name, type) in SUB_TYPE_MAP"
                  :key="type"
                  :label="name + '-' + type"
                  :value="name + '-' + type"
                />
              </el-select>
              <el-input
                v-else
                v-model="plan.subInfo"
                placeholder="子设备名称-子设备类型"
                :disabled="!plan.devType"
                :class="{ 'sub-info-error': plan.subInfoError }"
                @blur="onSubInfoBlur(plan)"
                @input="onSubInfoInput(plan)"
              />
            </div>
            <div class="col">
              <el-select
                v-model="plan.subLv"
                :disabled="!plan.devType"
                style="width: 100%"
                placeholder="选择层级"
                @change="onSubLvChange(plan)"
              >
                <el-option label="3（三级设备）" :value="3" />
                <el-option label="2（二级设备）" :value="2" />
                <el-option label="1（一级设备）" :value="1" />
              </el-select>
            </div>
            <div class="col">
              <div v-if="plan.subLv === 3" class="sub-id-container">
                <div class="sub-id-tag-wrap">
                  <el-tag
                    v-for="(id, idx) in plan.lv3.list"
                    :key="idx"
                    closable
                    size="small"
                    type="info"
                    effect="light"
                    @close="plan.lv3.list.splice(idx, 1)"
                  >
                    {{ id }}
                  </el-tag>
                </div>
                <el-input
                  v-model="plan.lv3.input"
                  size="small"
                  placeholder="输入三级编号，回车添加"
                  :disabled="!plan.devType || plan.subLv === null"
                  :class="{ 'sub-id-error': plan.lv3.error }"
                  @input="onLvInput(plan.lv3)"
                  @paste="handleLvPaste(plan, 0, plan.lv3, $event)"
                  @keyup.enter="handleLvEnter(plan.lv3)"
                />
              </div>
              <el-input
                v-else
                v-model="plan.lv3.val"
                size="small"
                placeholder="三级编号（固定）"
                :disabled="!plan.devType || plan.subLv === null"
                :class="{ 'sub-info-error': plan.lv3.error }"
                @input="onLvValInput(plan.lv3)"
                @blur="onLvValBlur(plan.lv3)"
              />
              <div
                v-if="plan.subLv === 3 && buildFullSubIds(plan).length"
                class="sub-id-tip"
              >
                将升级：{{ previewAddrs(plan) }}
              </div>
            </div>
            <div class="col">
              <div v-if="plan.subLv === 2" class="sub-id-container">
                <div class="sub-id-tag-wrap">
                  <el-tag
                    v-for="(id, idx) in plan.lv2.list"
                    :key="idx"
                    closable
                    size="small"
                    type="info"
                    effect="light"
                    @close="plan.lv2.list.splice(idx, 1)"
                  >
                    {{ id }}
                  </el-tag>
                </div>
                <el-input
                  v-model="plan.lv2.input"
                  size="small"
                  placeholder="输入二级编号，回车添加"
                  :disabled="!plan.devType || plan.subLv !== 2"
                  :class="{ 'sub-id-error': plan.lv2.error }"
                  @input="onLvInput(plan.lv2)"
                  @paste="handleLvPaste(plan, 1, plan.lv2, $event)"
                  @keyup.enter="handleLvEnter(plan.lv2)"
                />
                <div
                  v-if="plan.subLv === 2 && buildFullSubIds(plan).length"
                  class="sub-id-tip"
                >
                  将升级：{{ previewAddrs(plan) }}
                </div>
              </div>
              <el-input
                v-else
                v-model="plan.lv2.val"
                size="small"
                placeholder="二级编号（固定）"
                :disabled="!plan.devType || plan.subLv !== 1"
                :class="{ 'sub-info-error': plan.lv2.error }"
                @input="onLvValInput(plan.lv2)"
                @blur="onLvValBlur(plan.lv2)"
              />
            </div>
            <div class="col">
              <div class="sub-id-container">
                <div class="sub-id-tag-wrap">
                  <el-tag
                    v-for="(id, idx) in plan.lv1.list"
                    :key="idx"
                    closable
                    size="small"
                    type="info"
                    effect="light"
                    @close="plan.lv1.list.splice(idx, 1)"
                  >
                    {{ id }}
                  </el-tag>
                </div>
                <el-input
                  v-model="plan.lv1.input"
                  size="small"
                  placeholder="输入一级编号，回车添加"
                  :disabled="!plan.devType || plan.subLv !== 1"
                  :class="{ 'sub-id-error': plan.lv1.error }"
                  @input="onLvInput(plan.lv1)"
                  @paste="handleLvPaste(plan, 2, plan.lv1, $event)"
                  @keyup.enter="handleLvEnter(plan.lv1)"
                />
                <div
                  v-if="plan.subLv === 1 && buildFullSubIds(plan).length"
                  class="sub-id-tip"
                >
                  将升级：{{ previewAddrs(plan) }}
                </div>
              </div>
            </div>
            <div class="col">
              <el-select v-model="plan.fileUid" style="width: 100%">
                <el-option
                  v-for="file in fileList"
                  :key="file.uid"
                  :label="file.name"
                  :value="file.uid"
                />
              </el-select>
            </div>
            <div class="col col-action">
              <el-button
                v-if="index > 0"
                type="danger"
                link
                :icon="Delete"
                @click="deleteSvc004Plan(index)"
              />
            </div>
          </div>
          <el-button
            type="primary"
            class="add-plan-btn"
            :icon="Plus"
            @click="addSvc004Plan"
          >
            {{ $t("CUPV.addPlan") }}
          </el-button>
        </div>

        <template v-else-if="upgradeSelection == 1">
          <div class="endpoint-info">
            <el-icon class="info-icon-tip"><InfoFilled /></el-icon>
            <span>终端升级：终端 ID 将自动关联当前船舶，无需手动选择</span>
          </div>
          <div class="endpoint-firmware">
            <span class="firmware-label">终端当前固件：</span>
            <template
              v-if="endpointFirmware && endpointFirmware.queryStatus === 2"
            >
              <span class="firmware-fail">终端无响应</span>
            </template>
            <template
              v-else-if="endpointFirmware && endpointFirmware.queryStatus === 4"
            >
              <span class="firmware-fail">终端离线</span>
            </template>
            <template
              v-else-if="endpointFirmware && endpointFirmware.queryStatus === 3"
            >
              <span class="firmware-fail">{{
                endpointFirmware.message || "终端异常"
              }}</span>
            </template>
            <template v-else-if="endpointFirmware && endpointFirmware.firmware">
              <span class="firmware-value">{{
                endpointFirmware.firmware
              }}</span>
              <span
                v-if="endpointFirmware.firmwareReportTime"
                class="firmware-time"
                >（{{ endpointFirmware.firmwareReportTime }}）</span
              >
            </template>
            <span v-else class="firmware-fail">未知</span>
            <el-button
              class="firmware-refresh-btn"
              link
              size="small"
              :disabled="firmwareLoading || !endpointSelection"
              @click="loadEndpointFirmware(true)"
            >
              {{ firmwareLoading ? "查询中..." : "刷新" }}
            </el-button>
          </div>
          <!-- 三文件选择：固件 / MD5 校验文件 / 签名文件（[1,1] 下发三个下载 URL） -->
          <div class="svc001-file-section">
            <div class="svc001-file-row">
              <span class="file-label">固件文件：</span>
              <el-select
                v-model="svc001FileSelection.firmware"
                placeholder="选择固件文件"
                clearable
                class="file-select"
              >
                <el-option
                  v-for="f in fileList"
                  :key="f.uid"
                  :label="f.name"
                  :value="f.uid!"
                />
              </el-select>
            </div>
            <div class="svc001-file-row">
              <span class="file-label">MD5 文件：</span>
              <el-select
                v-model="svc001FileSelection.md5"
                placeholder="选择 MD5 校验文件"
                clearable
                class="file-select"
              >
                <el-option
                  v-for="f in fileList"
                  :key="f.uid"
                  :label="f.name"
                  :value="f.uid!"
                />
              </el-select>
            </div>
            <div class="svc001-file-row">
              <span class="file-label">签名文件：</span>
              <el-select
                v-model="svc001FileSelection.sign"
                placeholder="选择签名文件"
                clearable
                class="file-select"
              >
                <el-option
                  v-for="f in fileList"
                  :key="f.uid"
                  :label="f.name"
                  :value="f.uid!"
                />
              </el-select>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- ====== Step 4: 时间选择 ====== -->
    <div
      v-if="fileList.length > 0"
      ref="step4Card"
      class="step-card time-step-card"
    >
      <div class="step-card-header">
        <div class="step-badge" :class="{ done: upgradeDate }">
          <el-icon v-if="upgradeDate"><CircleCheck /></el-icon>
          <span v-else>4</span>
        </div>
        <div class="step-card-title">
          <h2>{{ $t("CUPV.selectTime") }}</h2>
          <span v-if="upgradeDate" class="title-extra">
            - {{ $d(new Date(upgradeDate), "long") }}
          </span>
        </div>
        <span class="step-status" :class="{ done: upgradeDate }">
          {{
            !isPlanReady ? "待升级信息完成" : upgradeDate ? "已选择" : "待选择"
          }}
        </span>
      </div>
      <div class="step-card-body">
        <div v-if="!isPlanReady" class="time-locked-tip">
          <el-icon><Lock /></el-icon>
          <span>请先完成上方「升级信息」步骤，配置完成后即可设置过期时间</span>
        </div>
        <el-date-picker
          v-model="upgradeDate"
          type="datetime"
          placeholder="Pick a Date"
          format="YYYY/MM/DD HH:mm:ss"
          :disabled="!isPlanReady"
          :disabled-date="optionsDisable"
          :shortcuts="shorcuts"
          :show-now="false"
          :default-value="getTargetTime(20)"
        />
      </div>
    </div>

    <!-- ====== 粘性操作栏（始终固定在底部） ====== -->
    <div v-if="shipId" class="sticky-action-bar">
      <div class="bar-progress">
        <span class="bar-label">创建进度</span>
        <div class="progress-indicator">
          <div
            class="step-dot"
            :class="{
              done: !!ship,
              current: !ship && !!shipId,
            }"
            title="船舶信息"
          >
            <el-icon v-if="ship"><CircleCheck /></el-icon>
            <span v-else>1</span>
          </div>
          <div
            class="step-line"
            :class="{ done: !!ship && fileList.length > 0 }"
          ></div>
          <div
            class="step-dot"
            :class="{
              done: fileList.length > 0,
              current: !!ship && fileList.length === 0,
            }"
            title="选择文件"
          >
            <el-icon v-if="fileList.length > 0"><CircleCheck /></el-icon>
            <span v-else>2</span>
          </div>
          <div
            class="step-line"
            :class="{ done: !!upgradeSelection && isValidPlanSet() }"
          ></div>
          <div
            class="step-dot"
            :class="{
              done: !!upgradeSelection && isValidPlanSet(),
              current:
                fileList.length > 0 && (!upgradeSelection || !isValidPlanSet()),
            }"
            title="升级计划"
          >
            <el-icon v-if="upgradeSelection && isValidPlanSet()"
              ><CircleCheck
            /></el-icon>
            <span v-else>3</span>
          </div>
          <div class="step-line" :class="{ done: !!upgradeDate }"></div>
          <div
            class="step-dot"
            :class="{
              done: !!upgradeDate,
              current: !!upgradeSelection && isValidPlanSet() && !upgradeDate,
            }"
            title="选择时间"
          >
            <el-icon v-if="upgradeDate"><CircleCheck /></el-icon>
            <span v-else>4</span>
          </div>
        </div>
      </div>
      <button
        class="create-btn"
        :class="{ pending: !canCreate }"
        @click="createUpgradePlan"
      >
        <span class="ripple"></span>
        <el-icon><Promotion /></el-icon>
        <span>{{ $t("CUPV.createUpgradePlan") }}</span>
      </button>
    </div>

    <!-- ====== Dialogs ====== -->
    <el-dialog
      v-model="isUpgradeStart"
      :title="$t('CUPV.addPlan')"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <el-progress
        :text-inside="true"
        :stroke-width="26"
        :status="upgradePlanStatus"
        :percentage="upgradePlanPercent"
      />
      <p>{{ upgradePlanTip }}</p>
    </el-dialog>
    <el-dialog
      v-model="isUpgradeEnd"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <el-result
        :icon="upgradePlanResult.icon"
        :title="upgradePlanResult.title"
        :sub-title="upgradePlanResult.subTitle"
      >
        <template #extra>
          <el-button type="primary" @click="router.back()">{{
            $t("CUPV.back")
          }}</el-button>
        </template>
      </el-result>
    </el-dialog>
  </div>
</template>
<script lang="ts" setup>
import { loadTranslations, translateName } from "@/composables/useTranslation";
import { getEndpointListApi } from "@/api/endpoint/endpointApi";
import { getDeviceListApi } from "@/api/deviceApi";
import {
  createDeviceUpgradePlanApi,
  createEndpointUpgradePlanApi,
  createSvc004UpgradeTaskApi,
  queryEndpointFirmwareApi,
  type EndpointFirmwareInfo,
} from "@/api/operation/upgradeApi";
import { getShipInfoApi, validateShipAuthorizationApi } from "@/api/productApi";
import router from "@/router";
import { Endpoint } from "@/types/endpoint";
import { DeviceItem } from "@/types/device";
import {
  LvInputState,
  Svc004DevicePlan,
  SUB_TYPE_MAP,
  UpgradePlan,
} from "@/types/operation";
import { Ship as ShipEntity } from "@/types/product";
import { httpAuthorized } from "@/util/http";
import {
  ArrowLeft,
  CircleCheck,
  Delete,
  Document,
  InfoFilled,
  Lock,
  Plus,
  Promotion,
  QuestionFilled,
  Ship,
  WarningFilled,
} from "@element-plus/icons-vue";
import {
  ElMessage,
  UploadFile,
  UploadFiles,
  UploadUserFile,
} from "element-plus";
import _, { throttle } from "lodash";
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

const { t } = useI18n();
const route = useRoute();
const config = {
  minimumTimeInterval: 10,
};

// 船舶选择
const shipId = computed(() => route.query.shipId as string);
const ship = ref<ShipEntity>();
const isShipContentVisible = ref<boolean>(false);
const getShipInfo = async () => {
  const response = await getShipInfoApi(shipId.value);
  if (response) {
    ship.value = response.ship;
  } else {
    router.replace({ name: "overall" });
  }
  // 管理员对船舶的写入权限认证
  const writeAuthority = await validateShipAuthorizationApi(shipId.value, 16);
  if (!writeAuthority) {
    ElMessage.error(
      "管理员对" + ship.value?.name + "的写入权限已过期，请重新申请"
    );
    router.replace({ name: "overall" });
  }
};

// 文件选择
const isFileContentVisible = ref<boolean>(true);
const fileList = ref<UploadUserFile[]>([]);
const selectedFile = ref<UploadUserFile>();
watch(fileList, (list: UploadUserFile[]) => {
  if (list.length > 0) {
    isFileContentVisible.value = false;
  }
});
const handleFileChange = async (file: UploadFile, files: UploadFiles) => {
  if (!file) return;
  if (!file.size) return;

  // Svc001 三文件改造：不再前端计算 MD5（终端升级三文件无 MD5 码；
  // 设备升级的 fileMd5 改用后端上传返回的磁盘 MD5）
  if (file.raw) {
    fileIdRefTable.value.push({
      id: "",
      uid: Number(file.uid),
      name: file.name,
      path: "",
      fileMd5: "",
    });
  }
};
const handleFileRemove = (file: UploadFile, files: UploadFiles) => {
  planList.value.forEach((item) => {
    if (item.fileId === file.uid) {
      item.fileId = null;
    }
  });
  svc004PlanList.value.forEach((item) => {
    if (item.fileUid === file.uid) {
      item.fileUid = null;
    }
  });
  if (endpointFileSelection.value == file.uid) {
    endpointFileSelection.value = undefined;
  }
  // Svc001 三文件：同步清理已删文件的角色引用，避免残留 uid 导致
  // isValidPlanSet 误通过 / 提交空 md5FileId 等脏数据
  if (svc001FileSelection.firmware === file.uid) {
    svc001FileSelection.firmware = undefined;
  }
  if (svc001FileSelection.md5 === file.uid) {
    svc001FileSelection.md5 = undefined;
  }
  if (svc001FileSelection.sign === file.uid) {
    svc001FileSelection.sign = undefined;
  }
  // 移除 fileIdRefTable 中已删文件的条目，避免残留 id="" 条目
  // 触发创建时 some((e) => !e.id) 误判"上传终止"
  fileIdRefTable.value = fileIdRefTable.value.filter(
    (entry) => entry.uid !== file.uid
  );
};

// 时间选择
const isTimeContentVisible = ref<boolean>(true);
const upgradeDate = ref<string>();
const getTargetTime = (second: number) => {
  const now = new Date();
  now.setSeconds(now.getSeconds() + second);
  return now;
};
const optionsDisable = (time: Date) => {
  return time.getTime() < Date.now() + 9 * 60 * 1000;
};

/**
 * 是否满足创建条件
 */
const canCreate = computed(() => {
  return (
    !!ship.value &&
    fileList.value.length > 0 &&
    !!upgradeSelection.value &&
    isValidPlanSet() &&
    !!upgradeDate.value
  );
});
const shorcuts = [
  {
    text: "20秒后",
    value: () => {
      const date = new Date();
      date.setSeconds(date.getSeconds() + 20);
      return date;
    },
  },
  {
    text: "1分钟后",
    value: () => {
      const date = new Date();
      date.setSeconds(date.getSeconds() + 60);
      return date;
    },
  },
  {
    text: "20分钟后",
    value: () => {
      const date = new Date();
      date.setMinutes(date.getMinutes() + 20);
      return date;
    },
  },
  {
    text: "1小时后",
    value: () => {
      const date = new Date();
      date.setMinutes(date.getMinutes() + 60);
      return date;
    },
  },
  {
    text: "2小时后",
    value: () => {
      const date = new Date();
      date.setMinutes(date.getMinutes() + 120);
      return date;
    },
  },
];

const showErrorMessage = throttle((message: string) => {
  ElMessage.error(message);
}, 1000); // 限制 1 秒内最多执行一次

watch(upgradeDate, (newDate) => {
  if (newDate) {
    upgradeDate.value = isDateBeforeMinimum(
      newDate,
      config.minimumTimeInterval
    );
  }
});
const isDateBeforeMinimum = (date: string, second: number) => {
  const planTime = new Date(date);
  const minimumTime = getTargetTime(second);
  if (planTime.getTime() < minimumTime.getTime()) {
    showErrorMessage(t("CUPV.timeError")); // 受 throttle 限制
    console.log(minimumTime.toDateString());
    return minimumTime;
  }
  return date;
};

// 终端升级计划
const endpointSelection = ref<string>();
const endpointList = ref<Endpoint[]>([]);
const endpointFileSelection = ref<number>();

// 终端当前固件名（Svc001 [1,4]/[1,5] 查询）
const endpointFirmware = ref<EndpointFirmwareInfo | null>(null);
const firmwareLoading = ref(false);
const loadEndpointFirmware = async (force: boolean) => {
  if (!endpointSelection.value) return;
  firmwareLoading.value = true;
  try {
    const res = await queryEndpointFirmwareApi(
      [endpointSelection.value],
      force
    );
    if (res && res.length > 0) {
      endpointFirmware.value = res[0];
    }
  } finally {
    firmwareLoading.value = false;
  }
};

const getEndpointList = async () => {
  const response = await getEndpointListApi(shipId.value);
  if (response) {
    endpointList.value = response;
    // 自动选择该船舶的终端（只有一个），无需手动选择
    if (response.length > 0) {
      endpointSelection.value = response[0].id;
      // 加载库内固件名快照（不发 MQTT）
      loadEndpointFirmware(false);
    }
  } else {
    router.back();
  }
};

const getDeviceList = async () => {
  const response = await getDeviceListApi(shipId.value);
  if (response) {
    deviceList.value = response;
  }
};

// 设备升级计划
const isCodeContentVisible = ref<boolean>(true);
const upgradeSelection = ref<number>(2);
const planList = ref<UpgradePlan[]>([
  {
    typeId: null,
    positions: [],
    fileId: null,
  },
]);

// Svc004 新协议设备升级计划（每行一个子设备）
const svc004PlanList = ref<Svc004DevicePlan[]>([
  {
    devType: "",
    devName: "",
    subType: null,
    subName: "",
    subInfo: "",
    subInfoError: false,
    subLv: null,
    lv3: { val: "", list: [], input: "", error: false },
    lv2: { val: "", list: [], input: "", error: false },
    lv1: { val: "", list: [], input: "", error: false },
    fileUid: null,
  },
]);

// 设备列表（从后端查询）
const deviceList = ref<DeviceItem[]>([]);

// 获取设备类型列表（去重+正序排序）
const deviceTypeList = computed(() => {
  const types = [...new Set(deviceList.value.map((d) => d.type))];
  return types.sort((a, b) => a.localeCompare(b));
});

// 根据设备类型获取设备名称列表（去重+正序排序）
const getDeviceNamesByType = (devType: string) => {
  if (!devType) return [];
  const names = [
    ...new Set(
      deviceList.value.filter((d) => d.type === devType).map((d) => d.name)
    ),
  ];
  names.sort((a, b) => a.localeCompare(b));
  return names.map((name) => ({ name }));
};

// 设备类型变更时，清空设备名称和子设备信息
const onDevTypeChange = (plan: Svc004DevicePlan) => {
  plan.devName = "";
  plan.subType = null;
  plan.subName = "";
  plan.subInfo = "";
  plan.subInfoError = false;
  plan.lv3.error = false;
  plan.lv2.error = false;
  plan.lv1.error = false;
};

// BMS类型：下拉选择子设备名称-类型
const onSubInfoChange = (plan: Svc004DevicePlan) => {
  if (!plan.subInfo) {
    plan.subName = "";
    plan.subType = null;
    return;
  }
  const parts = plan.subInfo.split("-");
  plan.subName = parts[0] || "";
  plan.subType = parts.length > 1 ? Number(parts[1]) : null;
};

// 非BMS类型：输入框失焦时解析子设备名称-类型
const onSubInfoBlur = (plan: Svc004DevicePlan) => {
  if (!plan.subInfo) {
    plan.subName = "";
    plan.subType = null;
    plan.subInfoError = false;
    return;
  }
  const parts = plan.subInfo.split("-");
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    plan.subInfoError = true;
    plan.subName = "";
    plan.subType = null;
    ElMessage.warning(
      "子设备名称-类型格式不正确，请使用'名称-类型'格式，如 EMU-6"
    );
    return;
  }
  const typeNum = Number(parts[1]);
  if (isNaN(typeNum) || typeNum < 1 || typeNum > 255) {
    plan.subInfoError = true;
    plan.subName = "";
    plan.subType = null;
    ElMessage.warning("子设备类型必须为1~255的数字");
    return;
  }
  plan.subInfoError = false;
  plan.subName = parts[0];
  plan.subType = typeNum;
};

// 非BMS类型：输入时清除错误状态
const onSubInfoInput = (plan: Svc004DevicePlan) => {
  plan.subInfoError = false;
};

const addPlan = () => {
  planList.value.push({
    typeId: null,
    positions: [],
    fileId: null,
  });
};

const addSvc004Plan = () => {
  svc004PlanList.value.push({
    devType: "",
    devName: "",
    subType: null,
    subName: "",
    subInfo: "",
    subInfoError: false,
    subLv: null,
    lv3: { val: "", list: [], input: "", error: false },
    lv2: { val: "", list: [], input: "", error: false },
    lv1: { val: "", list: [], input: "", error: false },
    fileUid: null,
  });
};

const deleteSvc004Plan = (index: number) => {
  svc004PlanList.value.splice(index, 1);
};

/**
 * 解析三段式子设备地址（三级-二级-一级），合法返回规范化字符串，非法返回 null。
 *
 * 格式规则：
 * - 三段以 "-" 分隔，如 1-2-12 = 三级设备1 下的二级设备2 下的一级设备12
 * - 每段为 1~255 整数或星号 *（全角 ＊ 统一转半角）
 * - * 表示无该级下级，即前段定位的设备本身就是升级目标：
 *   1-1-* = 三级1下的二级设备1本身；1-*-* = 三级设备1本身
 * - 兼容：输入含 0 自动转 *（1-0-0 → 1-*-*、1-4-0 → 1-4-*；0 后还有非 0 段则非法，如 1-0-5）
 * - 约束：第一段必须为数字；第二段为 * 时第三段必须为 *；
 *   不支持"全部"通配，需升级多个设备时逐个添加
 *
 * @param input 用户输入
 * @returns 规范化地址（如 "1-2-12"），非法返回 null
 */
const parseSubAddr = (input: string): string | null => {
  // 归一化：去首尾/内部空格、全角横杠/全角星号转半角（Excel/中文输入法常见）
  const val = input
    .trim()
    .replace(/\s+/g, "")
    .replace(/[－—–]/g, "-")
    .replace(/＊/g, "*");
  if (!val) return null;
  let parts = val.split("-");
  if (parts.length !== 3) return null;
  // 每段必须为整数或星号 *（0 暂容忍，下方自动转 *）
  if (parts.some((p) => p !== "*" && !/^\d+$/.test(p))) return null;
  // 旧写法兼容：0 表示无下级，从第一个 0 段起自动转为 *
  // （1-0-0 → 1-*-*、1-4-0 → 1-4-*；0 后还有非 0 段则非法，如 1-0-5）
  const firstZero = parts.findIndex((p) => p === "0");
  if (firstZero !== -1) {
    if (parts.slice(firstZero + 1).some((p) => p !== "0")) return null;
    parts = parts.map((p, i) => (i >= firstZero ? "*" : p));
  }
  // 数字段范围 1~255
  if (parts.some((p) => p !== "*" && (Number(p) < 1 || Number(p) > 255))) {
    return null;
  }
  // 第一段必须为数字（无"全部三级"通配概念）
  if (parts[0] === "*") return null;
  // 第二段为 *（无二级下级）时，第三段必须为 *
  if (parts[1] === "*" && parts[2] !== "*") return null;
  return parts.join("-");
};

/** 非法字符提示（节流，避免连续拦截时弹窗刷屏） */
const showSubIdInputError = throttle(() => {
  ElMessage.warning("仅允许输入数字");
}, 1000);

/**
 * Excel 粘贴批量添加：支持一行多列（TAB 分隔）、多行一列（换行分隔）及混合格式，
 * 自动忽略空单元格与无法识别的文字，仅添加合法地址（自动去重）
 */

/** 校验 1~255 整数，合法返回原串，非法返回 null */
const parseLvNum = (val: string): string | null => {
  const t = val.trim();
  return /^\d+$/.test(t) && Number(t) >= 1 && Number(t) <= 255 ? t : null;
};

/** 子设备地址是否填写就绪（按层级校验三个序号框） */
const isSubAddrReady = (plan: Svc004DevicePlan): boolean => {
  if (plan.subLv === 3) return plan.lv3.list.length > 0;
  if (plan.subLv === 2)
    return parseLvNum(plan.lv3.val) !== null && plan.lv2.list.length > 0;
  if (plan.subLv === 1)
    return (
      parseLvNum(plan.lv3.val) !== null &&
      parseLvNum(plan.lv2.val) !== null &&
      plan.lv1.list.length > 0
    );
  return false;
};

/** 固定前缀段（层级3=[]；层级2=[三级]；层级1=[三级,二级]；单值框未填好返回 null） */
const fixedSegs = (plan: Svc004DevicePlan): string[] | null => {
  if (plan.subLv === 3) return [];
  const lv3 = parseLvNum(plan.lv3.val);
  if (lv3 === null) return null;
  if (plan.subLv === 2) return [lv3];
  const lv2 = parseLvNum(plan.lv2.val);
  if (lv2 === null) return null;
  return [lv3, lv2];
};

/** 按层级拼装完整三段式地址（协议格式，后端零改动） */
const buildFullSubIds = (plan: Svc004DevicePlan): string[] => {
  const fixed = fixedSegs(plan);
  if (fixed === null) return [];
  if (plan.subLv === 3) return plan.lv3.list.map((n) => `${n}-*-*`);
  if (plan.subLv === 2) return plan.lv2.list.map((n) => `${fixed[0]}-${n}-*`);
  return plan.lv1.list.map((n) => `${fixed[0]}-${fixed[1]}-${n}`);
};

/** 地址预览文案：前 3 个完整地址 + 总数 */
const previewAddrs = (plan: Svc004DevicePlan): string => {
  const list = buildFullSubIds(plan);
  const head = list.slice(0, 3).join("、");
  return list.length > 3 ? `${head} 等 ${list.length} 个` : head;
};

/** 层级切换：清空三个序号框（不同层级下编号含义不同，避免残留脏数据） */
const onSubLvChange = (plan: Svc004DevicePlan) => {
  plan.lv3 = { val: "", list: [], input: "", error: false };
  plan.lv2 = { val: "", list: [], input: "", error: false };
  plan.lv1 = { val: "", list: [], input: "", error: false };
};

/** 多值框输入过滤：仅数字，非法字符拦截并红框 + 节流提示 */
const onLvInput = (lv: LvInputState) => {
  const raw = lv.input;
  const filtered = raw.replace(/[^0-9]/g, "");
  if (filtered !== raw) {
    lv.input = filtered;
    lv.error = true;
    showSubIdInputError();
  } else {
    lv.error = false;
  }
};

/** 多值框回车添加编号（1~255，去重） */
const handleLvEnter = (lv: LvInputState) => {
  const val = lv.input.trim();
  if (!val) return;
  if (parseLvNum(val) === null) {
    lv.error = true;
    ElMessage.warning("请输入 1~255 的设备编号");
    return;
  }
  if (lv.list.includes(val)) {
    lv.error = true;
    ElMessage.warning(`编号 ${val} 已存在`);
    return;
  }
  lv.error = false;
  lv.list.push(val);
  lv.input = "";
};

/** 单值框输入：仅数字 + 清除红框 */
const onLvValInput = (lv: LvInputState) => {
  lv.val = lv.val.replace(/[^0-9]/g, "");
  lv.error = false;
};

/** 单值框失焦校验（空值不标红，由缺失项提示兜底） */
const onLvValBlur = (lv: LvInputState) => {
  if (!lv.val.trim()) {
    lv.error = false;
    return;
  }
  lv.error = parseLvNum(lv.val) === null;
};

/**
 * 多值框粘贴双模式：纯编号直接加；完整三段式地址按固定前缀校验后取对应段
 * @param segIdx 该框对应的段位（0=三级 1=二级 2=一级）
 */
const handleLvPaste = (
  plan: Svc004DevicePlan,
  segIdx: number,
  lv: LvInputState,
  event: ClipboardEvent
) => {
  const text = event.clipboardData?.getData("text") || "";
  if (!text.trim()) return;
  event.preventDefault();

  const fixed = fixedSegs(plan);
  if (fixed === null || fixed.length !== segIdx) {
    lv.error = true;
    ElMessage.warning("请先正确填写上级编号");
    return;
  }

  const tokens = text.split(/[\r\n\t,，;；/|\s]+/);
  let added = 0;
  let fromAddr = 0;
  let ignored = 0;
  tokens.forEach((token) => {
    const t = token.trim();
    if (!t) return; // 空单元格跳过
    if (/^\d+$/.test(t)) {
      if (parseLvNum(t) !== null) {
        if (!lv.list.includes(t)) {
          lv.list.push(t);
          added++;
        }
      } else {
        ignored++; // 编号越界
      }
      return;
    }
    const addr = parseSubAddr(t);
    if (!addr) {
      ignored++; // 格式非法
      return;
    }
    const seg = addr.split("-");
    let num: string | null = null;
    if (segIdx === 0 && seg[1] === "*" && seg[2] === "*") {
      num = seg[0];
    } else if (segIdx === 1 && seg[2] === "*" && seg[0] === fixed[0]) {
      num = seg[1];
    } else if (segIdx === 2 && seg[0] === fixed[0] && seg[1] === fixed[1]) {
      num = seg[2];
    }
    if (num !== null) {
      if (!lv.list.includes(num)) {
        lv.list.push(num);
        added++;
        fromAddr++;
      }
    } else {
      ignored++; // 层级或上级编号不符
    }
  });

  if (added > 0) {
    lv.error = false;
    lv.input = "";
    const addrTip = fromAddr > 0 ? `（${fromAddr} 个从地址提取）` : "";
    if (ignored > 0) {
      ElMessage.warning(
        `已添加 ${added} 个编号${addrTip}，忽略 ${ignored} 项（越界/格式/层级/路径不符）`
      );
    } else if (fromAddr > 0) {
      ElMessage.info(`已添加 ${added} 个编号${addrTip}`);
    }
  } else if (ignored > 0) {
    lv.error = true;
    ElMessage.warning("粘贴内容中未识别到符合当前层级与上级编号的设备编号");
  }
};

/**
 * 添加子设备地址标签
 * @param plan 当前设备升级计划行
 */

// 设备位置内容确认
const handlePositionInput = (value: string, plan: UpgradePlan) => {
  const positions = plan.positions;
  const regex = /^(\d+)-(\d+)-(\d+)$/;
  const match = value.match(regex);

  // 不符合 "数字-数字-数字" 格式
  if (!match) {
    console.log("不符合格式:", value);
    plan.positions = positions.filter((item) => item != value);
    return;
  }

  // 提取 3 个数字并转换为数值
  const [_, n1, n2, n3] = match;
  const num1 = parseInt(n1, 10);
  const num2 = parseInt(n2, 10);
  const num3 = parseInt(n3, 10);

  // 检查是否在 0-255 之间，并确保不重复
  if (
    num1 < 0 ||
    num1 >= 256 ||
    num2 < 0 ||
    num2 >= 256 ||
    num3 < 0 ||
    num3 >= 256
  ) {
    console.log("不符合范围:", value);
    plan.positions = positions.filter((item) => item != value);
    return;
  }
  console.log("去重:", value);
  plan.positions = Array.from(new Set(positions));
};

// 设备位置粘贴解析
const handlePositionPaste = (plan: UpgradePlan) => {
  navigator.clipboard.readText().then((text) => {
    if (!text) return;

    // 允许的分隔符：逗号(,)、分号(;)、斜杠(/)、空格、换行(\n, \r)
    const newPositions = text
      .split(/[\n\r\t,，;；/ ]+/) // 按分隔符拆分
      .map((position) => position.trim()) // 去掉首尾空格
      .filter((position) => {
        const regex = /^(\d+)-(\d+)-(\d+)$/;
        const match = position.match(regex);
        if (!match) {
          console.log("不符合格式:", position);
          return false;
        }
        const [_, n1, n2, n3] = match;
        const num1 = parseInt(n1, 10);
        const num2 = parseInt(n2, 10);
        const num3 = parseInt(n3, 10);
        console.log(num1, num2, num3);

        // 检查是否在 0-255 之间，并确保不重复
        if (
          num1 < 0 ||
          num1 >= 256 ||
          num2 < 0 ||
          num2 >= 256 ||
          num3 < 0 ||
          num3 >= 256
        ) {
          console.log("不符合范围:", position);
          return false;
        }
        return true;
      }); // 确保符合 "1-1-1" 格式

    if (newPositions.length) {
      // **去重**，确保不添加已有的值
      if (!plan.positions) {
        plan.positions = [];
      }
      const uniquePositions = newPositions.filter(
        (pos) => !plan.positions.includes(pos)
      );

      if (uniquePositions.length) {
        plan.positions.push(...uniquePositions);
        console.log("成功添加的新数据:", uniquePositions);
      } else {
        console.warn("所有粘贴的数据都已存在，无需添加");
      }
    }
  });
};
// 1-1-1， fas, 2562-1-1, 2-2-2
const deletePlan = (index: number) => {
  planList.value.splice(index, 1);
};

const isValidPlanSet = () => {
  if (upgradeSelection.value == 1) {
    // Svc001 三文件改造：需要终端 + 固件/MD5/签名三个文件都已选定
    return (
      endpointSelection.value &&
      svc001FileSelection.firmware !== undefined &&
      svc001FileSelection.md5 !== undefined &&
      svc001FileSelection.sign !== undefined
    );
  }
  // Svc004 新协议：每行设备都需要填写完整
  return !svc004PlanList.value.some(
    (plan) =>
      !plan.devType ||
      !plan.devName ||
      plan.subLv === null ||
      !isSubAddrReady(plan) ||
      plan.subType === null ||
      !plan.subName ||
      plan.fileUid === null
  );
};

/** 升级信息是否配置完成（Step 4 解锁条件） */
const isPlanReady = computed(
  () => !!upgradeSelection.value && isValidPlanSet()
);

/** 升级信息缺失项清单（实时展示在 Step 3 中，点击创建时逐条提示） */
const planIssues = computed<string[]>(() => {
  const issues: string[] = [];
  if (!upgradeSelection.value) {
    issues.push(t("CUPV.selectUpgradeModeError"));
    return issues;
  }
  if (upgradeSelection.value === 1) {
    if (!endpointSelection.value) {
      issues.push("终端未关联（请确认船舶已绑定终端）");
    }
    if (svc001FileSelection.firmware === undefined) {
      issues.push("未选择固件文件");
    }
    if (svc001FileSelection.md5 === undefined) {
      issues.push("未选择 MD5 校验文件");
    }
    if (svc001FileSelection.sign === undefined) {
      issues.push("未选择签名文件");
    }
  } else {
    svc004PlanList.value.forEach((plan, index) => {
      const missing: string[] = [];
      if (!plan.devType) missing.push("设备类型");
      if (!plan.devName) missing.push("设备名称");
      if (plan.subLv === null) missing.push("子设备层级");
      else if (!isSubAddrReady(plan)) {
        if (plan.subLv === 3) {
          missing.push("三级序号（至少一个编号）");
        } else if (parseLvNum(plan.lv3.val) === null) {
          missing.push("三级序号（1~255 整数）");
        } else if (plan.subLv === 2 && plan.lv2.list.length === 0) {
          missing.push("二级序号（至少一个编号）");
        } else if (plan.subLv === 1 && parseLvNum(plan.lv2.val) === null) {
          missing.push("二级序号（1~255 整数）");
        } else {
          missing.push("一级序号（至少一个编号）");
        }
      }
      if (plan.subType === null || !plan.subName) {
        missing.push(
          plan.subInfoError
            ? "子设备信息（格式错误，应为 名称-类型，如 EMU-6）"
            : "子设备信息"
        );
      }
      if (plan.fileUid === null) missing.push("升级文件");
      if (missing.length) {
        issues.push(`第 ${index + 1} 行：${missing.join("、")}未填写`);
      }
    });
  }
  return issues;
});

/**
 * 点击创建时逐项校验并提示具体错误（替代禁用按钮的"无反馈"体验）
 */
const validateForm = (): boolean => {
  if (!ship.value) {
    ElMessage.error("船舶信息尚未加载完成，请稍候");
    return false;
  }
  if (fileList.value.length === 0) {
    ElMessage.error("请先上传升级文件");
    return false;
  }
  if (upgradeSelection.value === undefined) {
    ElMessage.error(t("CUPV.selectUpgradeModeError"));
    return false;
  }
  if (planIssues.value.length > 0) {
    ElMessage.error("升级信息未填写完整：" + planIssues.value[0]);
    return false;
  }
  if (upgradeDate.value === undefined) {
    ElMessage.error("请设置过期时间");
    return false;
  }
  if (new Date(upgradeDate.value) < getTargetTime(config.minimumTimeInterval)) {
    ElMessage.error(t("CUPV.timeError"));
    return false;
  }
  return true;
};

// 校验非BMS类型的 subInfo 格式
const validateSubInfo = (): boolean => {
  for (const plan of svc004PlanList.value) {
    if (plan.devType !== "BMS" && plan.subInfo) {
      const parts = plan.subInfo.split("-");
      if (
        parts.length !== 2 ||
        !parts[0] ||
        !parts[1] ||
        isNaN(Number(parts[1]))
      ) {
        ElMessage.error(
          `子设备格式错误："${plan.subInfo}"，请使用"名称-类型"格式，如 EMU-6`
        );
        return false;
      }
    }
  }
  return true;
};
const fileIdRefTable = ref<
  {
    id: string;
    uid: number;
    name: string;
    path: string;
    fileMd5: string;
  }[]
>([]);

// ==================== Svc001 终端升级三文件选择（固件 / MD5 / 签名） ====================
const svc001FileSelection = reactive<{
  firmware: number | undefined;
  md5: number | undefined;
  sign: number | undefined;
}>({ firmware: undefined, md5: undefined, sign: undefined });

/**
 * 按扩展名判断文件角色：.md5 -> MD5 文件；.minisig/.sig/.sign/.asc -> 签名文件；其余 -> 固件
 */
const isMd5File = (name: string) => /\.md5$/i.test(name);
const isSignFile = (name: string) => /\.(minisig|sig|sign|asc)$/i.test(name);

/**
 * 按扩展名自动预填三个文件位（未填的才填，用户可手动调整）
 */
const autoFillSvc001FileSelection = () => {
  const list = fileList.value;
  if (!svc001FileSelection.md5) {
    const f = list.find((x) => isMd5File(x.name));
    svc001FileSelection.md5 = f?.uid;
  }
  if (!svc001FileSelection.sign) {
    const f = list.find((x) => isSignFile(x.name));
    svc001FileSelection.sign = f?.uid;
  }
  if (!svc001FileSelection.firmware) {
    const f = list.find((x) => !isMd5File(x.name) && !isSignFile(x.name));
    svc001FileSelection.firmware = f?.uid;
  }
};

// fileList 变化 / 切换到终端升级模式时自动预填
watch(fileList, autoFillSvc001FileSelection, { deep: true });
watch(upgradeSelection, (val) => {
  if (val === 1) autoFillSvc001FileSelection();
});

// ==================== 步骤卡自动滚动引导 ====================
const step3Card = ref<HTMLElement>();
const step4Card = ref<HTMLElement>();

const scrollToCard = (el: HTMLElement | undefined) => {
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// 文件选择完成（Step 3 出现）时自动滚动引导，无需手动下滑
watch(
  () => fileList.value.length > 0,
  (hasFiles, prev) => {
    if (hasFiles && !prev) {
      nextTick(() => scrollToCard(step3Card.value));
    }
  }
);

// 升级信息配置完成（Step 4 解锁）且尚未设置时间时，滚动到时间步骤
watch(isPlanReady, (ready, prev) => {
  if (ready && !prev && !upgradeDate.value) {
    // 稍作延迟，避免与 Step 3 出现时的滚动互相抢占
    setTimeout(() => scrollToCard(step4Card.value), 400);
  }
});

// 升级过程展示
const isUpgradeStart = ref<boolean>(false);
const upgradePlanPercent = ref<number>(0);
const upgradePlanStatus = ref<string>("");
const upgradePlanTip = ref<string>("");
const createUpgradePlan = async () => {
  // 管理员对船舶的写入权限认证
  const writeAuthority = await validateShipAuthorizationApi(shipId.value, 16);
  if (!writeAuthority) {
    ElMessage.error(
      "管理员对" + ship.value?.name + "的写入权限已过期，请重新申请"
    );
    router.replace({ name: "overall" });
    return;
  }
  // 数据验证（逐项校验并提示具体错误）
  if (!validateForm()) return;
  isUpgradeStart.value = true;
  upgradePlanTip.value = t("CUPV.fileUploadRate", {
    current: fileIdRefTable.value.length,
    total: fileList.value.length,
  });

  let allUploaded = true;
  for (const file of fileList.value) {
    try {
      if (!file.uid || !file.raw) {
        throw new Error("Invalid file data");
      }

      // Svc001 三文件改造：不再前端计算/上传 MD5（后端跳过比对，仍返回磁盘 MD5 供设备升级使用）
      const formData = new FormData();
      formData.append("uid", String(file.uid));
      formData.append("file", file.raw);
      formData.append("shipId", shipId.value);

      // 异步上传逻辑
      const result = await httpAuthorized.post("/file/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // 更新 fileIdRefTable 中对应条目的 id/path/fileMd5（fileMd5 为后端磁盘 MD5，设备升级使用）
      const entry = _.find(fileIdRefTable.value, {
        uid: Number(result.data.data.result.uid),
      });
      if (entry) {
        entry.id = result.data.data.result.id;
        entry.name = result.data.data.result.name || entry.name;
        entry.path = result.data.data.result.path || "";
        entry.fileMd5 = result.data.data.result.fileMd5 || entry.fileMd5;
      }
      upgradePlanTip.value = t("CUPV.fileUploadRate", {
        current: fileIdRefTable.value.length,
        total: fileList.value.length,
      });
      upgradePlanPercent.value =
        (80 / fileList.value.length) * fileIdRefTable.value.length;
    } catch (error: any) {
      // 后端返回 FILE_MD5_MISMATCH 时提示 MD5 不一致
      const errMsg =
        error?.response?.data?.message || error?.data?.message || "";
      if (errMsg.includes("MD5") || errMsg.includes("md5")) {
        ElMessage.error(
          "文件MD5校验不一致，文件可能已损坏，请重新选择文件上传"
        );
      } else {
        ElMessage.error(t("CUPV.fileUploadFail"));
      }
      allUploaded = false; // 上传失败，终止后续上传
      break;
    }
  }

  // 任一文件上传失败（id 仍为空）则中止创建，避免携带空 fileId 的脏升级计划
  if (!allUploaded || fileIdRefTable.value.some((e) => !e.id)) {
    console.error("文件上传终止");
    upgradePlanStatus.value = "exception";
    updateUpgradeEndState(2);
    return;
  }

  // 终端升级（Svc001 新协议：三文件）
  if (upgradeSelection.value === 1) {
    if (endpointSelection.value === undefined) return;
    if (
      svc001FileSelection.firmware === undefined ||
      svc001FileSelection.md5 === undefined ||
      svc001FileSelection.sign === undefined
    ) {
      ElMessage.error("请完整选择固件、MD5、签名三个文件");
      return;
    }
    const firmwareEntry = _.find(fileIdRefTable.value, {
      uid: svc001FileSelection.firmware,
    }) as { id: string; uid: number; name: string; path: string } | undefined;
    const md5Entry = _.find(fileIdRefTable.value, {
      uid: svc001FileSelection.md5,
    }) as { id: string; uid: number; name: string; path: string } | undefined;
    const signEntry = _.find(fileIdRefTable.value, {
      uid: svc001FileSelection.sign,
    }) as { id: string; uid: number; name: string; path: string } | undefined;
    const result = await createEndpointUpgradePlanApi({
      planName: "终端升级-" + (ship.value?.name || ""),
      shipId: shipId.value,
      endpointId: endpointSelection.value!,
      planTime: upgradeDate.value,
      fileId: firmwareEntry?.id || "",
      fileName: firmwareEntry?.name || "",
      filePath: firmwareEntry?.path || "",
      md5FileId: md5Entry?.id || "",
      md5FileName: md5Entry?.name || "",
      md5FilePath: md5Entry?.path || "",
      signFileId: signEntry?.id || "",
      signFileName: signEntry?.name || "",
      signFilePath: signEntry?.path || "",
    });
    console.log(result);
    if (result) {
      upgradePlanPercent.value = 100;
      upgradePlanStatus.value = "success";
      updateUpgradeEndState(1);
    } else {
      upgradePlanStatus.value = "exception";
      ElMessage.error(t("CUPV.createUpgradePlanFail"));
      updateUpgradeEndState(2);
    }
  }
  // 设备升级（Svc004 新协议）
  else if (upgradeSelection.value === 2) {
    // 校验非BMS类型的 subInfo 格式
    if (!validateSubInfo()) return;

    // 构建升级项列表
    const items: {
      devName: string;
      subName: string;
      subLv: number;
      subType: number;
      subId: string[];
      fileId: string;
      fileName: string;
      filePath: string;
      fileMd5: string;
    }[] = svc004PlanList.value
      .filter(
        (plan) =>
          plan.devType &&
          plan.devName &&
          plan.subLv !== null &&
          isSubAddrReady(plan) &&
          plan.subType !== null &&
          plan.subName &&
          plan.fileUid !== null
      )
      .map((plan) => {
        const fileEntry = _.find(fileIdRefTable.value, {
          uid: plan.fileUid,
        }) as
          | {
              id: string;
              uid: number;
              name: string;
              path: string;
              fileMd5: string;
            }
          | undefined;
        return {
          devName: plan.devName,
          subLv: plan.subLv!,
          subName: plan.subName,
          subType: plan.subType!,
          subId: buildFullSubIds(plan),
          fileId: fileEntry?.id || "",
          fileName: fileEntry?.name || "",
          filePath: fileEntry?.path || "",
          fileMd5: fileEntry?.fileMd5 || "",
        };
      });

    if (items.length === 0) {
      ElMessage.error("请至少添加一个有效的设备升级项");
      return;
    }

    const result = await createSvc004UpgradeTaskApi({
      planName: "设备升级-" + (ship.value?.name || ""),
      endpointId: endpointSelection.value || "",
      shipId: shipId.value,
      planTime: upgradeDate.value!,
      items: items,
    });

    upgradePlanPercent.value = 100;
    if (result) {
      upgradePlanStatus.value = "success";
      updateUpgradeEndState(1);
    } else {
      upgradePlanStatus.value = "exception";
      ElMessage.error(t("CUPV.createUpgradePlanFail"));
      updateUpgradeEndState(2);
    }
  }
};

// 升级结束
const isUpgradeEnd = ref<boolean>(false);
// 默认结果为失败
const upgradePlanResult = reactive<{
  title: string;
  icon: string;
  subTitle: string;
}>({
  icon: "error",
  title: t("CUPV.upgradePlanResult.error.title"),
  subTitle: t("CUPV.upgradePlanResult.error.subTitle"),
});

const updateUpgradeEndState = (flag: number) => {
  setTimeout(() => {
    isUpgradeEnd.value = true;
    // 成功
    if (flag == 1) {
      upgradePlanResult.icon = "success";
      upgradePlanResult.title = t("CUPV.upgradePlanResult.success.title");
      upgradePlanResult.subTitle = t("CUPV.upgradePlanResult.success.subTitle");
    }
  }, 500);
};

onMounted(async () => {
  // 确保翻译表就绪：登录后首次进入时 App 挂载阶段的加载因未登录(401)失败，
  // 设备升级的设备名下拉翻译依赖此缓存；与 App 启动加载共享同一次请求
  await loadTranslations();
  getShipInfo();
  getEndpointList();
  getDeviceList();
});
</script>
<style lang="less" scoped>
.create-upgrade-plan {
  display: block;
  padding: 24px 28px 140px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f4f8fb 0%, #e7eef5 100%);
}

.create-upgrade-plan > * + * {
  margin-top: 18px;
}
.icon-arrow_a {
  font-size: 26px;
  cursor: pointer;
}
.plan-positions-input {
  max-height: 135px;
}
:deep(.el-input-tag__inner) {
  max-height: 125px;
  overflow: auto;
  margin: 5px;
}
.title {
  font-family: "SimHei";
  letter-spacing: 1px;
  font-weight: bolder;
  font-size: 20px;
  cursor: pointer;
  height: 30px;
  line-height: 30px;
  width: 100%;
  margin: 10px 20px;
}
.content {
  padding: 20px;
  min-width: 500px;
}
:deep(.el-card__header) {
  padding: 0;
}
:deep(.el-card__body) {
  padding: 0;
}
.user-input {
  display: flex;
  align-items: center;
}
.input-search {
  margin-right: 20px;
}
.btn-search {
  width: 120px;
}
:deep(.tbl-row) {
  cursor: pointer;
}
.icon-dui {
  color: rgb(64, 163, 33);
  font-size: 20px;
}
.icon-cuo {
  color: rgb(238, 30, 30);
  font-size: 20px;
}
.plan-box {
  width: 100%;
  max-height: 150px;
  margin: 10px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 0 3px 1px rgb(203, 228, 255);
  background-color: #f2fbff;
  border-radius: 5px;
  padding: 10px;
}
.plan-header {
  display: flex;
  justify-content: space-between;
  padding: 0 10px;
  & > p {
    font-family: "SimHei";
    font-weight: bolder;
  }
}
.plan-title {
  width: 5%;
  text-align: center;
}
.plan-device-type {
  width: 10%;
}
.plan-positions {
  width: 55%;
}
.plan-field {
  width: 10%;
}
.plan-file {
  width: 15%;
}
.sub-id-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}
.col-help {
  display: flex;
  align-items: center;
  gap: 4px;
}
.col-help-icon {
  font-size: 14px;
  color: #94a3b8;
  cursor: help;
  transition: color 0.2s ease;
}
.col-help-icon:hover {
  color: #0891b2;
}
/* 表头与单元格内容居中 */
.plan-table-head .col,
.plan-row .col {
  text-align: center;
}
.plan-row .col :deep(.el-input__inner) {
  text-align: center;
}
.sub-id-tip {
  font-size: 11px;
  line-height: 1.5;
  color: #94a3b8;
}
.sub-id-tag-wrap {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  min-height: 0;
  /* 标签过多时限高内部滚动，避免行高无限膨胀 */
  max-height: 108px;
  overflow-y: auto;
}
.plan-delete {
  width: 5%;
  text-align: center;
  & > i {
    font-size: 25px;
    cursor: pointer;
    color: rgb(255, 48, 48);
    transition: color 0.3s;
    &:hover {
      color: rgb(185, 12, 12);
    }
  }
}
.endpoint-selection {
  display: flex;
  align-items: center;
  margin: 10px;
}
.sub-info-error,
.sub-id-error {
  :deep(.el-input__wrapper) {
    box-shadow: 0 0 0 1px var(--el-color-danger) inset !important;
  }
}

/* ====== Hero ====== */
.info-hero {
  position: relative;
  background: linear-gradient(135deg, #0c2461 0%, #1e3799 45%, #0a3d62 100%);
  color: #fff;
  border-radius: 20px;
  padding: 28px 36px;
  overflow: hidden;
  box-shadow: 0 16px 36px rgba(12, 36, 97, 0.32);
  flex-shrink: 0;
}
.info-hero .hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.info-hero .hero-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.info-hero .back-btn {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  color: rgba(224, 242, 254, 0.9);
  font-size: 12px;
  cursor: pointer;
  backdrop-filter: blur(8px);
  margin-bottom: 14px;
  transition: background 0.2s ease, transform 0.2s ease;
}
.info-hero .back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-2px);
}
.info-hero .back-btn .el-icon {
  font-size: 13px;
}
.info-hero .hero-main {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 20px;
}
.info-hero .hero-avatar {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #e0f2fe 100%);
  color: #0c2461;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
}
.info-hero .hero-text {
  flex: 1;
}
.info-hero .hero-tag {
  display: inline-block;
  padding: 3px 10px;
  font-size: 10px;
  letter-spacing: 3px;
  font-weight: 600;
  color: #a5f3fc;
  background: rgba(165, 243, 252, 0.14);
  border: 1px solid rgba(165, 243, 252, 0.32);
  border-radius: 999px;
  margin-bottom: 8px;
}
.info-hero .hero-title {
  font-size: 26px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 6px;
  letter-spacing: 0.8px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  line-height: 1.2;
}
.info-hero .hero-subtitle {
  color: rgba(207, 250, 254, 0.85);
  font-size: 13px;
  margin: 0;
}

/* ====== 步骤卡 ====== */
.step-card {
  background: #fff;
  border-radius: 16px;
  padding: 0;
  box-shadow: 0 4px 16px rgba(8, 145, 178, 0.06), 0 1px 2px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(8, 145, 178, 0.08);
  overflow: hidden;
  scroll-margin-top: 16px;
}
.step-card-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid rgba(8, 145, 178, 0.08);
}
.step-badge {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(8, 145, 178, 0.08);
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  border: 1px solid rgba(8, 145, 178, 0.15);
  transition: background 0.3s ease, color 0.3s ease;
}
.step-badge .el-icon {
  font-size: 18px;
}
.step-badge.done {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 10px rgba(16, 185, 129, 0.32);
}
.step-card-title {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.step-card-title h2 {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: 0.4px;
}
.title-extra {
  font-size: 13px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.step-status {
  flex-shrink: 0;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(148, 163, 184, 0.15);
  color: #64748b;
}
.step-status.done {
  background: rgba(16, 185, 129, 0.14);
  color: #047857;
}
.step-card-body {
  padding: 20px 22px;
}

/* 时间选择步骤卡：底部多留些空间 */
.time-step-card {
  margin-bottom: 60px;
}

.time-step-card .step-card-body {
  padding-bottom: 40px;
}

/* ====== 船舶信息网格 ====== */
.ship-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
.info-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid rgba(8, 145, 178, 0.08);
  border-radius: 12px;
}
.info-card .info-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0c2461 0%, #0891b2 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow: 0 4px 10px rgba(8, 145, 178, 0.28);
}
.info-card .info-icon-cyan {
  background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
}
.info-card .info-body {
  flex: 1;
  min-width: 0;
}
.info-card .info-label {
  font-size: 11px;
  color: #64748b;
  letter-spacing: 0.3px;
  display: block;
  margin-bottom: 2px;
}
.info-card .info-value {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* ====== 上传区 ====== */
.upload-demo :deep(.el-upload-dragger) {
  border-radius: 12px;
  border: 2px dashed rgba(8, 145, 178, 0.28);
  background: linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%);
  transition: border-color 0.3s ease, background 0.3s ease;
}
.upload-demo :deep(.el-upload-dragger):hover {
  border-color: #0891b2;
  background: linear-gradient(180deg, #e0f2fe 0%, #cffafe 100%);
}
.upload-demo :deep(.el-upload__text) {
  color: #475569;
}
.upload-demo :deep(.el-upload__text em) {
  color: #0891b2;
  font-style: normal;
  font-weight: 600;
}
.upload-demo :deep(.el-upload__tip) {
  color: #94a3b8;
}
.upload-demo :deep(.el-icon--upload) {
  font-size: 48px;
  color: #0891b2;
  margin-bottom: 8px;
}

/* ====== 升级模式单选 ====== */
.upgrade-radio-group {
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
}

/* ====== 配置缺失项提示条 ====== */
.plan-issues {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.35);
  border-radius: 10px;
}
.plan-issues > .el-icon {
  flex-shrink: 0;
  color: #d97706;
  font-size: 18px;
  margin-top: 2px;
}
.plan-issues .plan-issues-title {
  font-size: 13px;
  font-weight: 600;
  color: #b45309;
  margin-bottom: 4px;
}
.plan-issues ul {
  margin: 0;
  padding-left: 18px;
}
.plan-issues li {
  font-size: 12px;
  color: #92400e;
  line-height: 1.8;
}

/* ====== 时间步骤锁定提示 ====== */
.time-locked-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  margin-bottom: 14px;
  background: rgba(148, 163, 184, 0.1);
  border: 1px dashed rgba(148, 163, 184, 0.5);
  border-radius: 10px;
  font-size: 13px;
  color: #64748b;
}
.time-locked-tip .el-icon {
  color: #94a3b8;
  font-size: 16px;
}
.upgrade-radio-group :deep(.el-radio.is-bordered) {
  padding: 12px 22px;
  border-radius: 10px;
  border-color: rgba(8, 145, 178, 0.18);
  background: #fff;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.3s ease;
}
.upgrade-radio-group :deep(.el-radio.is-bordered):hover {
  border-color: #0891b2;
}
.upgrade-radio-group :deep(.el-radio.is-bordered.is-checked) {
  background: linear-gradient(
    135deg,
    rgba(8, 145, 178, 0.08) 0%,
    rgba(6, 182, 212, 0.08) 100%
  );
  border-color: #0891b2;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.16);
}

.endpoint-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(8, 145, 178, 0.06);
  border: 1px dashed rgba(8, 145, 178, 0.25);
  border-radius: 10px;
  font-size: 13px;
  color: #0e7490;
}
.endpoint-info .info-icon-tip {
  font-size: 16px;
  color: #0891b2;
}

.endpoint-firmware {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
  padding: 10px 16px;
  background: rgba(8, 145, 178, 0.06);
  border: 1px solid rgba(8, 145, 178, 0.15);
  border-radius: 10px;
  font-size: 13px;
  .firmware-label {
    color: #64748b;
  }
  .firmware-value {
    color: #0e7490;
    font-weight: 600;
    font-family: monospace;
  }
  .firmware-time {
    color: #94a3b8;
    font-size: 12px;
  }
  .firmware-fail {
    color: #d97706;
  }
  .firmware-refresh-btn {
    margin-left: auto;
    color: #0891b2;
  }
}

.svc001-file-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  .svc001-file-row {
    display: flex;
    align-items: center;
    gap: 10px;
    .file-label {
      flex-shrink: 0;
      width: 90px;
      font-size: 13px;
      color: #64748b;
    }
    .file-select {
      flex: 1;
    }
  }
}

/* ====== Svc004 表 ====== */
.svc004-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.plan-table-head,
.plan-row {
  display: grid;
  grid-template-columns: 40px 1fr 1fr 1.2fr 0.8fr 0.9fr 0.9fr 0.9fr 1.2fr 48px;
  gap: 12px;
  align-items: center;
}
.plan-table-head {
  padding: 10px 12px;
  background: rgba(8, 145, 178, 0.06);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #0e7490;
  letter-spacing: 0.4px;
}
.plan-row {
  padding: 12px;
  background: #fff;
  border: 1px solid rgba(8, 145, 178, 0.12);
  border-radius: 10px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.plan-row:hover {
  border-color: #0891b2;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.1);
}
.col-title {
  text-align: center;
}
.row-index {
  display: inline-block;
  width: 26px;
  height: 26px;
  line-height: 26px;
  border-radius: 8px;
  background: rgba(8, 145, 178, 0.1);
  color: #0891b2;
  font-weight: 700;
  font-size: 12px;
}
.col-action {
  text-align: center;
}
.add-plan-btn {
  align-self: flex-start;
  margin-top: 8px;
  border-radius: 8px;
  background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.28);
}
.add-plan-btn:hover {
  box-shadow: 0 6px 16px rgba(8, 145, 178, 0.36);
}

/* ====== 操作栏 ====== */
.sticky-action-bar {
  position: sticky;
  bottom: 20px;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 14px 24px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(8, 145, 178, 0.18);
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(8, 145, 178, 0.18), 0 2px 6px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
}

.bar-progress {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.bar-label {
  font-size: 13px;
  font-weight: 700;
  color: #0e7490;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.progress-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
}

.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid rgba(8, 145, 178, 0.25);
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  transition: background 0.3s ease, color 0.3s ease, border-color 0.3s ease,
    box-shadow 0.3s ease;

  .el-icon {
    font-size: 16px;
  }

  &.done {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 4px 10px rgba(16, 185, 129, 0.32);
  }

  &.current {
    border-color: #0891b2;
    color: #0891b2;
    box-shadow: 0 0 0 4px rgba(8, 145, 178, 0.16);
  }
}

.step-line {
  width: 28px;
  height: 2px;
  background: rgba(8, 145, 178, 0.15);
  transition: background 0.3s ease;

  &.done {
    background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  }
}

.action-bar {
  display: none;
}
.create-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 36px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(8, 145, 178, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
  transition: transform 0.2s ease, box-shadow 0.3s ease, opacity 0.2s ease;
}
.create-btn .ripple {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.4) 0%,
    transparent 70%
  );
  opacity: 0;
  transform: scale(0.5);
  transition: opacity 0.4s ease, transform 0.6s ease;
  pointer-events: none;
}
.create-btn .el-icon {
  font-size: 18px;
}
.create-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(8, 145, 178, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}
.create-btn:hover:not(:disabled) .ripple {
  opacity: 1;
  transform: scale(1.5);
}
.create-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
/* 待完善态：可点击（点击时提示缺失项），仅降低视觉强度 */
.create-btn.pending {
  opacity: 0.65;
}

@media (max-width: 1100px) {
  .plan-table-head,
  .plan-row {
    grid-template-columns: 40px 1fr 1fr 1fr 0.8fr 0.9fr 0.9fr 0.9fr 1fr 48px;
    font-size: 12px;
  }
}
@media (max-width: 800px) {
  .plan-table-head,
  .plan-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .col-title,
  .col-action {
    text-align: left;
  }
  .ship-info-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="less">
/* el-tooltip 内容 teleport 到 body，scoped 无法命中，需全局样式 */
.subid-help-popper {
  max-width: 360px;
  .tt-title {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 8px;
  }
  .tt-row {
    font-size: 12px;
    color: #475569;
    line-height: 1.9;
  }
  .tt-lv {
    display: inline-block;
    padding: 0 8px;
    margin-right: 6px;
    border-radius: 999px;
    background: rgba(8, 145, 178, 0.1);
    color: #0e7490;
    font-weight: 600;
    font-size: 11px;
  }
  .tt-sep {
    height: 1px;
    background: #e2e8f0;
    margin: 8px 0;
  }
}
</style>
